// POM-6249: the Fill Later choices, as data.
//
// The order toolbar carried three buttons that all park work for later, two of them labelled
// "Move to Fill Later" word for word. They are one menu now, and this is what that menu is built
// from: add a choice, drop one, or reorder them by editing this list. Nothing here renders, so a
// change of mind about the menu never becomes a change to the button.
//
// Each option is { id, label, hint, enabled, reason }. `reason` is shown, and the choice greyed,
// whenever enabled is false — a choice the operator can see and understand beats one that silently
// disappears, which is what two of the three buttons used to do.
import { canMoveOrderToFillLater, isMovableLineItem } from './fillLaterEligibility';

export const FILL_LATER_OPTION = {
  ORDER: 'ORDER',
  ITEMS: 'ITEMS',
  QUANTITY: 'QUANTITY',
};

// POM-6249: why a line item cannot be moved. The dialog used to caption every blocked item
// "already filled", which is only true for one of the six states that block it - a shipped or
// cancelled item was being explained wrongly. Shown on the checkbox as a tooltip.
const MOVE_BLOCKED_BY_PROCESSING_STATE = {
  SHIPPED: 'Already shipped',
  IN_SHIPMENT: 'In a shipment',
  READY_FOR_SHIPMENT: 'Ready for shipment',
  TRANSFERRED: 'Transferred out',
  CANCELED: 'Cancelled',
  FILL_LATER: 'Already parked for Fill Later',
};

export const moveBlockedReason = (item) => {
  const byState = MOVE_BLOCKED_BY_PROCESSING_STATE[item?.processingState];

  if (byState) return byState;
  if (item?.lineItemStatus === 'PRODUCT_FULFILLED') return 'Already filled';

  // POM-6289 made an item under review unmovable, but nothing here said so, so the dialog listed
  // it greyed out with an empty tooltip and no caption - the one row the operator would ask about
  // was the only one that explained nothing.
  //
  // Read last, after the states above: those are settled facts about where the item has got to,
  // while a review is the one blocker that clears on its own. An item that has already shipped is
  // better described as shipped than as in review, even when both are true.
  if (item?.taggedForReview) return 'In review';

  return null;
};

export const buildFillLaterOptions = (params) => {
  const {
    order,
    hasAccess,
    movableLineItems,
    canMoveToFillLater,
    splitSelectedLineItems,
    splitSkippedCount,
  } = params;

  const orderEligibility = canMoveOrderToFillLater({
    order,
    hasMoveAccess: hasAccess?.moveOrderToFillLater,
  });

  const itemCount = order?.lineItems?.length ?? 0;
  const selectedCount = splitSelectedLineItems?.length ?? 0;
  const movableCount = movableLineItems?.length ?? 0;

  return [
    {
      id: FILL_LATER_OPTION.ORDER,
      label: 'Move this order to Fill Later',
      // "All 1 item" is not a sentence - a single-item order says so plainly instead.
      hint: itemCount > 1 ? `All ${itemCount} items` : 'The whole order',
      // canMoveOrderToFillLater says "hidden" for a choice that does not apply at all, as opposed
      // to one that applies and is refused. That was being greyed with a reason like any other
      // refusal, which is the opposite of what the flag asks for.
      hidden: orderEligibility.hidden,
      // canMoveOrderToFillLater already decides both whether the choice applies at all and why it
      // does not, so the menu reuses its wording rather than inventing a second vocabulary.
      enabled: !orderEligibility.hidden && orderEligibility.eligible,
      reason:
        orderEligibility.reason ??
        (orderEligibility.hidden ? 'This order can no longer be moved to Fill Later' : null),
      lineItems: orderEligibility.lineItems,
    },
    {
      id: FILL_LATER_OPTION.ITEMS,
      label: 'Move items to Fill Later',
      // A single-item order is a Cancel, not a partial fill later - there is no "some of these"
      // to choose. Left out of the menu rather than greyed: a choice that can never apply to
      // this order is noise, not information.
      hidden: itemCount <= 1,
      // Says which items qualify and how many, then the one thing that distinguishes this
      // choice from moving the whole order: every item gets its own date.
      hint:
        movableCount && movableCount < itemCount
          ? `${movableCount} of ${itemCount} items can be moved and fill later`
          : 'Items not yet filled can be moved and fill later',
      enabled: canMoveToFillLater,
      reason: canMoveToFillLater ? null : 'No item on this order can be moved',
    },
    {
      id: FILL_LATER_OPTION.QUANTITY,
      label: 'Split by quantity and Fill Later',
      hint: 'Keep part now, the rest goes to Fill Later',
      enabled: selectedCount > 0,
      reason: selectedCount ? null : 'Tick an item that can be split by quantity',
      // Carried through so the menu can warn that some of what is ticked will be left behind,
      // the way the Fill Later button's tooltip used to.
      skippedCount: splitSkippedCount,
    },
  ];
};
/**
 * POM-6320: the line items as the selected-items dialog wants them — every item on the order, each
 * flagged movable or not and captioned with why. Shared so Order View and the patient profile list
 * the same rows with the same captions for the same order.
 *
 * Here rather than beside isMovableLineItem because the caption comes from moveBlockedReason above,
 * and fillLaterEligibility must not import this file back.
 */
export const buildMoveableDialogLineItems = (lineItems = []) =>
  lineItems.map((item) => ({
    lineItemId: item.lineItemId,
    drugName: item.itemName,
    rxNumber: item.rxNumber,
    quantity: item.quantity,
    isMovable: isMovableLineItem(item),
    blockedReason: moveBlockedReason(item),
  }));

export default buildFillLaterOptions;
